import { isBefore } from 'date-fns'
import { Info } from 'lucide-react'
import { Muted } from '@/components/sv/typographies'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { PeriodChangeIndicator } from '@/routes/usage/components/period-change-indicator'
import { computeComparison, formatMoney } from '@/routes/usage/utils'

const DATA_AVAILABLE_FROM = new Date('2026-01-31')

type SummaryCardsProps = {
  currentTotal: number
  projectedTotal: number
  previousTotal: number
  isPreviousPeriod: boolean
  previousPeriodStart: Date | null
}

const Divider = () => <div className="w-px bg-border" />

const SummaryItem = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="flex-1 min-w-0 px-6 py-4">
    <Muted className="text-xs">{label}</Muted>
    <div className="mt-1">{children}</div>
  </div>
)

export const SummaryCards = ({
  currentTotal,
  projectedTotal,
  previousTotal,
  isPreviousPeriod,
  previousPeriodStart,
}: SummaryCardsProps) => {
  const comparison = computeComparison({
    currentTotal: projectedTotal,
    previousTotal,
  })

  const isPreviousIncomplete =
    previousPeriodStart !== null &&
    isBefore(previousPeriodStart, DATA_AVAILABLE_FROM)

  return (
    <div className="flex">
      <SummaryItem label={isPreviousPeriod ? 'Period total' : 'Current period'}>
        <p className="text-xl font-semibold tabular-nums">
          {formatMoney(currentTotal)}
        </p>
      </SummaryItem>

      {!isPreviousPeriod && (
        <>
          <Divider />
          <SummaryItem label="Projected total">
            <p className="text-xl font-semibold tabular-nums">
              {formatMoney(projectedTotal)}
            </p>
          </SummaryItem>
        </>
      )}

      {!isPreviousPeriod && (
        <>
          <Divider />
          <SummaryItem label="Previous period">
            <div className="flex items-center gap-1.5">
              <p className="text-xl font-semibold tabular-nums">
                {formatMoney(previousTotal)}
              </p>
              {isPreviousIncomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Usage data is only available from Jan 31, 2026. Previous
                    period data may be incomplete.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </SummaryItem>

          <Divider />
          <SummaryItem label="Change">
            <div className="flex items-center gap-1.5">
              <PeriodChangeIndicator
                percentageChange={comparison.percentageChange}
                direction={comparison.direction}
              />
              {isPreviousIncomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Change may be inaccurate due to incomplete previous period
                    data.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </SummaryItem>
        </>
      )}
    </div>
  )
}
